<template>
    <div id="visitor-calls">
        <a-spin :spinning="loading">
            <div class="calls-header">
                <div class="header-info">
                    <div class="visitor-phone">
                        <i class="material-icons">phone</i>
                        <span>{{ getDisplayPhoneNumber() }}</span>
                    </div>
                    <div class="total-calls" v-if="callRecords.length > 0">
                        <span class="count">{{ callRecords.length }}</span>
                        <span class="label">toplam arama</span>
                    </div>
                    <div class="providers-info" v-if="providers.length > 0">
                        <span class="providers-label">Sağlayıcılar:</span>
                        <span 
                            v-for="provider in providers" 
                            :key="provider.type"
                            class="provider-badge"
                            v-tippy="{ content: `${provider.count} arama` }"
                        >
                            {{ provider.name }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Quick Stats -->
            <div class="quick-stats" v-if="callRecords.length > 0">
                <div class="stat-item incoming">
                    <div class="stat-icon">
                        <i class="material-icons">call_received</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-number">{{ incomingCalls }}</div>
                        <div class="stat-label">Gelen</div>
                    </div>
                </div>
                <div class="stat-item outgoing">
                    <div class="stat-icon">
                        <i class="material-icons">call_made</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-number">{{ outgoingCalls }}</div>
                        <div class="stat-label">Giden</div>
                    </div>
                </div>
                <div class="stat-item answered">
                    <div class="stat-icon">
                        <i class="material-icons">check_circle</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-number">{{ answeredCalls }}</div>
                        <div class="stat-label">Cevaplanmış</div>
                    </div>
                </div>
                <div class="stat-item total-duration">
                    <div class="stat-icon">
                        <i class="material-icons">schedule</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-number">{{ totalDurationText }}</div>
                        <div class="stat-label">Toplam Süre</div>
                    </div>
                </div>
            </div>

            <!-- Calls List -->
            <div class="calls-content">
                <div v-if="callRecords.length === 0 && !loading" class="empty-state">
                    <div class="empty-icon">
                        <i class="material-icons">call_received</i>
                    </div>
                    <div class="empty-text">Bu kişi ile henüz arama kaydı bulunamadı</div>
                    <div class="empty-subtitle">Tüm webphone sağlayıcılarında son 12 aydaki aramalar kontrol edildi</div>
                </div>

                <div v-else class="calls-list">
                    <div 
                        v-for="call in callRecords" 
                        :key="call.uniqueid"
                        class="call-item"
                        :class="getCallItemClass(call)"
                    >
                        <div class="call-direction">
                            <div class="direction-icon" :class="call.direction.toLowerCase()">
                                <i class="material-icons">
                                    {{ call.direction === 'INBOUND' ? 'call_received' : 'call_made' }}
                                </i>
                            </div>
                        </div>

                        <div class="call-info">
                            <div class="call-primary">
                                <div class="call-number-info">
                                    <span class="other-number">{{ getOtherNumber(call) }}</span>
                                    <span v-if="getCallAgent(call)" class="call-agent">
                                        {{ getCallAgent(call) }}
                                    </span>
                                    <span v-if="call.provider" class="call-provider">
                                        {{ call.provider }}
                                    </span>
                                </div>
                                <div class="call-status" :class="call.status.toLowerCase()">
                                    {{ getStatusText(call.status) }}
                                </div>
                            </div>
                            <div class="call-secondary">
                                <div class="call-time">
                                    {{ formatCallDate(call.calldate) }}
                                </div>
                                <div class="call-duration" v-if="call.billsec > 0">
                                    <i class="material-icons">schedule</i>
                                    {{ formatDuration(call.billsec) }}
                                </div>
                            </div>
                        </div>

                        <div class="call-actions">
                            <button 
                                v-if="call.recordingfile && call.recordurl"
                                class="action-btn play-btn"
                                @click="playRecording(call)"
                            >
                                <i class="material-icons">play_circle</i>
                            </button>
                            <button 
                                class="action-btn info-btn"
                                @click="showCallDetails(call)"
                            >
                                <i class="material-icons">info</i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Call Detail Modal -->
            <a-modal v-model="showDetailModal" title="Arama Detayları" :footer="null" width="600px">
                <div v-if="selectedCall">
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="Tarih">
                            {{ $moment(selectedCall.calldate).format("DD.MM.YYYY HH:mm:ss") }}
                        </a-descriptions-item>
                        <a-descriptions-item label="Yön">
                            <a-tag :color="getDirectionColor(selectedCall.direction)">
                                {{ getDirectionText(selectedCall.direction) }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="Arayan">
                            {{ selectedCall.cli }}
                        </a-descriptions-item>
                        <a-descriptions-item label="Aranan">
                            {{ selectedCall.cld }}
                        </a-descriptions-item>
                        <a-descriptions-item label="Durum">
                            <a-tag :color="getStatusColor(selectedCall.status)">
                                {{ selectedCall.status }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="Supsis Kullanıcısı">
                            {{ getCallAgent(selectedCall) || 'Bilinmiyor' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="Toplam Süre">
                            {{ formatDuration(selectedCall.duration) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="Konuşma Süresi">
                            {{ formatDuration(selectedCall.billsec) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="CDR ID" span="2">
                            {{ selectedCall.cdrid }}
                        </a-descriptions-item>
                        <a-descriptions-item label="Unique ID" span="2">
                            {{ selectedCall.uniqueid }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
            </a-modal>

            <!-- Audio Player -->
            <div class="audio-player-dock" v-if="showAudioPlayer">
                <div class="audio-player-container">
                    <div class="audio-player-info">
                        <i class="material-icons audio-icon">music_note</i>
                        <div class="audio-title">{{ currentPlayingRecordName }}</div>
                    </div>
                    <div class="audio-player-wrapper">
                        <audio controls :src="currentPlayingRecordURL" autoplay ref="audioPlayer"></audio>
                    </div>
                    <div class="audio-player-controls">
                        <a-button type="danger" shape="circle" icon="close" @click="closeAudioPlayer" />
                    </div>
                </div>
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import { WEBPHONE_UNIFIED_API } from "../../../api/webphone.unified";
import { mapState } from "vuex";
import log2 from "../../../utils/log2";

const log = log2("VisitorCalls");

export default Vue.extend({
    name: "VisitorCalls",
    props: {
        visitorId: {
            type: String,
            required: true,
        },
        contact: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            callRecords: [],
            cleanedPhoneNumber: "",
            originalPhoneNumber: "",
            showDetailModal: false,
            selectedCall: null,
            // Audio player
            showAudioPlayer: false,
            currentPlayingRecordURL: "",
            currentPlayingRecordName: "",
            // User mapping for extensions
            extensionUserMapping: {},
            // İstek kontrolü için
            isInitialized: false,
            // Provider bilgileri
            providers: [],
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
        }),
        
        incomingCalls() {
            return this.callRecords.filter(call => call.direction === 'INBOUND').length;
        },
        outgoingCalls() {
            return this.callRecords.filter(call => call.direction === 'OUTBOUND').length;
        },
        answeredCalls() {
            return this.callRecords.filter(call => call.status === 'ANSWERED').length;
        },
        totalDurationText() {
            const totalSeconds = this.callRecords.reduce((sum, call) => sum + (call.billsec || 0), 0);
            return this.formatDuration(totalSeconds);
        },
    },
    async mounted() {
        await this.initializeData();
    },
    methods: {
        async initializeData() {
            if (this.loading || this.isInitialized) return;
            
            try {
                this.loading = true;
                
                // Get phone number from visitor (will be cleaned by backend)
                this.originalPhoneNumber = this.contact?.phone || this.contact?.whatsappNumber || "";
                
                log("Visitor phone data:", {
                    visitorId: this.visitorId,
                    visitorPhone: this.contact?.phone,
                    visitorWhatsapp: this.contact?.whatsappNumber,
                    originalPhoneNumber: this.originalPhoneNumber
                });
                
                // Telefon numarası yoksa işlemi durdur
                if (!this.originalPhoneNumber) {
                    this.cleanedPhoneNumber = "";
                    return;
                }

                // Load call records from all webphone providers (backend will handle everything)
                await this.loadCallRecords();
                
                this.isInitialized = true;
            } finally {
                this.loading = false;
            }
        },

        cleanPhoneNumber(phoneNumber) {
            if (!phoneNumber) return "";
            
            // Sadece rakamları bırak
            let cleaned = phoneNumber.replace(/\D/g, "");
            
            // Türk numarası kontrolü ve temizleme
            if (cleaned.startsWith("90")) {
                cleaned = cleaned.substring(2); // 90'ı kaldır
            }
            if (cleaned.startsWith("0")) {
                cleaned = cleaned.substring(1); // 0'ı kaldır
            }
            
            return cleaned;
        },


        async loadCallRecords() {
            log("loadCallRecords");
      
            this.callRecords = [];
            this.providers = [];

            try {
                // Telefon numarası kontrolü
                if (!this.originalPhoneNumber) {
                    this.cleanedPhoneNumber = "";
                    log("loadCallRecords no originalPhoneNumber");
                    return;
                }

                // Unified API'den tüm webphone sağlayıcılarının verilerini çek
                const response = await WEBPHONE_UNIFIED_API.getVisitorCDRList(this.visitorId);

                if (response && response.cdrs) {
                    this.callRecords = response.cdrs.map((cdr, index) => ({
                        ...cdr,
                        _id: cdr.uniqueid || index.toString(),
                        // Convert duration strings to numbers
                        duration: parseInt(cdr.duration) || 0,
                        billsec: parseInt(cdr.billsec) || 0,
                        ringtime: parseInt(cdr.ringtime) || 0,
                    }));
                } else {
                    this.callRecords = [];
                }

                // Provider bilgilerini kaydet
                if (response && response.providers) {
                    this.providers = response.providers;
                }

                // Update phone number info from backend response
                if (response && response.visitorInfo) {
                    this.cleanedPhoneNumber = response.visitorInfo.cleanedPhoneNumber || this.originalPhoneNumber;
                    this.originalPhoneNumber = response.visitorInfo.originalPhoneNumber || this.originalPhoneNumber;
                } else {
                    // Backend'den gelmezse manuel temizle
                    this.cleanedPhoneNumber = this.cleanPhoneNumber(this.originalPhoneNumber);
                }

                log("Unified CDR records loaded:", {
                    count: this.callRecords.length,
                    cleanedPhone: this.cleanedPhoneNumber,
                    providers: this.providers,
                });

            } catch (error) {
                console.error("Call records loading failed:", error);
                this.$message.error("Arama kayıtları yüklenirken hata oluştu");
                // Hata durumunda da telefon numarasını göster
                this.cleanedPhoneNumber = this.cleanPhoneNumber(this.originalPhoneNumber);
            }
        },



        getDisplayPhoneNumber() {
            // Önce visitor'dan direkt kontrol et
            const visitorPhone = this.visitor?.phone || this.visitor?.whatsappNumber;
            if (visitorPhone) {
                return this.cleanedPhoneNumber || visitorPhone;
            }
            
            // Eğer originalPhoneNumber varsa onu göster
            if (this.originalPhoneNumber) {
                return this.cleanedPhoneNumber || this.originalPhoneNumber;
            }
            
            return 'Telefon numarası bulunamadı';
        },

        getCallItemClass(call) {
            return {
                'incoming': call.direction === 'INBOUND',
                'outgoing': call.direction === 'OUTBOUND',
                'answered': call.status === 'ANSWERED',
                'missed': call.status !== 'ANSWERED',
            };
        },

        getOtherNumber(call) {
            // Visitor'ın numarası ile karşılaştır
            const visitorNumber = this.cleanedPhoneNumber;
            const cli = this.cleanPhoneNumber(call.cli);
            const cld = this.cleanPhoneNumber(call.cld);
            
            if (cli === visitorNumber) {
                return call.cld; // Visitor aradı, cld'yi göster
            } else {
                return call.cli; // Visitor'ı aradılar, cli'yi göster
            }
        },

        getCallAgent(call) {
            // Dahili numarasını bul
            let extension = null;
            
            if (call.direction === 'INBOUND') {
                // Gelen arama: cld dahili olabilir
                extension = call.cld;
            } else {
                // Giden arama: cli dahili olabilir
                extension = call.cli;
            }
            
            // Extension mapping'den kullanıcı adını bul
            return this.extensionUserMapping[extension] || null;
        },

        getStatusText(status) {
            const statusMap = {
                'ANSWERED': 'Cevaplanmış',
                'NO ANSWER': 'Cevap Yok',
                'BUSY': 'Meşgul',
                'FAILED': 'Başarısız',
            };
            return statusMap[status] || status;
        },

        formatCallDate(date) {
            const now = moment();
            const callMoment = moment(date);
            
            if (now.isSame(callMoment, 'day')) {
                return callMoment.format('HH:mm');
            } else if (now.diff(callMoment, 'days') === 1) {
                return 'Dün ' + callMoment.format('HH:mm');
            } else if (now.diff(callMoment, 'days') < 7) {
                return callMoment.format('dddd HH:mm');
            } else {
                return callMoment.format('DD.MM.YYYY HH:mm');
            }
        },

        formatDuration(seconds) {
            const duration = parseInt(seconds) || 0;
            if (duration === 0) return "0s";
            
            const hours = Math.floor(duration / 3600);
            const minutes = Math.floor((duration % 3600) / 60);
            const secs = duration % 60;
            
            if (hours > 0) {
                return `${hours}sa ${minutes}dk`;
            } else if (minutes > 0) {
                return `${minutes}dk ${secs}s`;
            } else {
                return `${secs}s`;
            }
        },

        showCallDetails(call) {
            this.selectedCall = call;
            this.showDetailModal = true;
        },

        async playRecording(call) {
            if (!call.recordurl) {
                this.$message.error("Kayıt dosyası bulunamadı");
                return;
            }

            this.currentPlayingRecordURL = call.recordurl;
            this.currentPlayingRecordName = `${this.formatCallDate(call.calldate)} - ${this.getOtherNumber(call)}`;
            this.showAudioPlayer = true;

            this.$nextTick(() => {
                if (this.$refs.audioPlayer) {
                    this.$refs.audioPlayer.focus();
                }
            });
        },

        closeAudioPlayer() {
            if (this.$refs.audioPlayer) {
                this.$refs.audioPlayer.pause();
            }
            this.showAudioPlayer = false;
            this.currentPlayingRecordURL = "";
            this.currentPlayingRecordName = "";
        },

        getDirectionText(direction) {
            return direction === 'INBOUND' ? 'Gelen' : 'Giden';
        },

        getDirectionColor(direction) {
            return direction === 'INBOUND' ? 'green' : 'blue';
        },

        getStatusColor(status) {
            const colors = {
                'ANSWERED': 'green',
                'NO ANSWER': 'orange',
                'BUSY': 'red',
                'FAILED': 'red',
            };
            return colors[status] || 'default';
        },
    },
    beforeDestroy() {
        this.closeAudioPlayer();
    },
});
</script>

<style scoped lang="scss">
#visitor-calls {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #f8f9fa;

    .calls-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        background: white;
        border-bottom: 1px solid #e9ecef;

        .header-info {
            display: flex;
            align-items: center;
            gap: 20px;
            flex-wrap: wrap;

            .visitor-phone {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                font-weight: 500;
                color: #495057;

                .material-icons {
                    color: #28a745;
                    font-size: 20px;
                }
            }

            .total-calls {
                display: flex;
                align-items: center;
                gap: 5px;
                
                .count {
                    font-size: 18px;
                    font-weight: 600;
                    color: #007bff;
                }
                
                .label {
                    font-size: 14px;
                    color: #6c757d;
                }
            }

            .providers-info {
                display: flex;
                align-items: center;
                gap: 8px;

                .providers-label {
                    font-size: 13px;
                    color: #6c757d;
                    font-weight: 500;
                }

                .provider-badge {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                    cursor: help;
                    transition: all 0.2s ease;

                    &:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
                    }
                }
            }
        }
    }

    .quick-stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 15px;
        padding: 20px;
        background: white;
        border-bottom: 1px solid #e9ecef;

        .stat-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            background: #f8f9fa;
            border-radius: 8px;
            border-left: 4px solid;

            &.incoming {
                border-left-color: #28a745;
                
                .stat-icon {
                    background: rgba(40, 167, 69, 0.1);
                    color: #28a745;
                }
            }

            &.outgoing {
                border-left-color: #007bff;
                
                .stat-icon {
                    background: rgba(0, 123, 255, 0.1);
                    color: #007bff;
                }
            }

            &.answered {
                border-left-color: #20c997;
                
                .stat-icon {
                    background: rgba(32, 201, 151, 0.1);
                    color: #20c997;
                }
            }

            &.total-duration {
                border-left-color: #ffc107;
                
                .stat-icon {
                    background: rgba(255, 193, 7, 0.1);
                    color: #ffc107;
                }
            }

            .stat-icon {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;

                .material-icons {
                    font-size: 20px;
                }
            }

            .stat-content {
                .stat-number {
                    font-size: 18px;
                    font-weight: 600;
                    color: #212529;
                    line-height: 1;
                }

                .stat-label {
                    font-size: 12px;
                    color: #6c757d;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
            }
        }
    }

    .calls-content {
        flex: 1;
        overflow: hidden;
        background: white;

        .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 300px;
            color: #6c757d;

            .empty-icon {
                margin-bottom: 16px;

                .material-icons {
                    font-size: 48px;
                    color: #dee2e6;
                }
            }

            .empty-text {
                font-size: 16px;
                font-weight: 500;
                margin-bottom: 8px;
            }

            .empty-subtitle {
                font-size: 14px;
                color: #adb5bd;
            }
        }

        .calls-list {
            height: 100%;
            overflow-y: auto;

            .call-item {
                display: flex;
                align-items: center;
                padding: 16px 20px;
                border-bottom: 1px solid #f1f3f4;
                transition: all 0.2s ease;

                &:hover {
                    background: #f8f9fa;
                }

                &:last-child {
                    border-bottom: none;
                }

                .call-direction {
                    margin-right: 16px;

                    .direction-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        &.inbound {
                            background: rgba(40, 167, 69, 0.1);
                            color: #28a745;
                        }

                        &.outbound {
                            background: rgba(0, 123, 255, 0.1);
                            color: #007bff;
                        }

                        .material-icons {
                            font-size: 20px;
                        }
                    }
                }

                .call-info {
                    flex: 1;
                    min-width: 0;

                    .call-primary {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        margin-bottom: 4px;

                        .call-number-info {
                            display: flex;
                            flex-direction: column;
                            gap: 2px;

                            .other-number {
                                font-size: 15px;
                                font-weight: 500;
                                color: #212529;
                            }

                            .call-agent {
                                font-size: 13px;
                                color: #007bff;
                                font-weight: 500;
                            }

                            .call-provider {
                                font-size: 11px;
                                color: #6c757d;
                                background: #f8f9fa;
                                padding: 2px 8px;
                                border-radius: 8px;
                                width: fit-content;
                                font-weight: 600;
                            }
                        }

                        .call-status {
                            font-size: 12px;
                            font-weight: 500;
                            padding: 2px 8px;
                            border-radius: 12px;

                            &.answered {
                                background: rgba(40, 167, 69, 0.1);
                                color: #28a745;
                            }

                            &.no {
                                background: rgba(255, 193, 7, 0.1);
                                color: #ffc107;
                            }

                            &.busy, &.failed {
                                background: rgba(220, 53, 69, 0.1);
                                color: #dc3545;
                            }
                        }
                    }

                    .call-secondary {
                        display: flex;
                        align-items: center;
                        gap: 16px;

                        .call-time {
                            font-size: 13px;
                            color: #6c757d;
                        }

                        .call-duration {
                            display: flex;
                            align-items: center;
                            gap: 4px;
                            font-size: 12px;
                            color: #28a745;
                            
                            .material-icons {
                                font-size: 14px;
                            }
                        }
                    }
                }

                .call-actions {
                    display: flex;
                    gap: 8px;
                    opacity: 0;
                    transition: opacity 0.2s ease;

                    .action-btn {
                        width: 32px;
                        height: 32px;
                        border: none;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s ease;

                        .material-icons {
                            font-size: 16px;
                            color: white;
                        }

                        &.play-btn {
                            background: #28a745;

                            &:hover {
                                background: #218838;
                            }
                        }

                        &.info-btn {
                            background: #6c757d;

                            &:hover {
                                background: #5a6268;
                            }
                        }
                    }
                }

                &:hover .call-actions {
                    opacity: 1;
                }
            }
        }
    }

    .audio-player-dock {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 15px;
        background: rgba(0, 0, 0, 0.9);
        z-index: 1000;

        .audio-player-container {
            display: flex;
            align-items: center;
            max-width: 800px;
            margin: 0 auto;
            gap: 20px;

            .audio-player-info {
                display: flex;
                align-items: center;
                gap: 12px;
                color: white;

                .audio-icon {
                    font-size: 24px;
                    animation: pulse 2s infinite;
                }

                .audio-title {
                    font-weight: 500;
                }
            }

            .audio-player-wrapper {
                flex: 1;

                audio {
                    width: 100%;
                    height: 35px;
                }
            }
        }
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
    }
}

// Scrollbar styling
.calls-list {
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #dee2e6;
        border-radius: 3px;

        &:hover {
            background: #adb5bd;
        }
    }
}
</style> 